// =====================================================
// File: src/LandLordPages/Reservations.jsx
// =====================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import { motion } from "framer-motion";
import {
  Calendar,
  Download,
  Search,
  AlertTriangle,
  Frown,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import { exportReservations, exportCleanup } from "../api/LandlordDashboard";

const CACHE_KEY = "ll_reservations_cache_v2";
const PAGE_SIZE = 15;
const EXPIRE_MS = 600_000;

const VISIBLE_COLUMNS = [
  "Rental ID",
  "SpotHero Rental ID",
  "Vehicle License Plate",
  "Facility Title",
  "Reservation Type",
  "Reservation Status",
  "Starts",
  "Ends",
  "Date Purchased",
  "Remit Amount",
];

const DISPLAY_HEADER = (h) => (h === "Remit Amount" ? "Remit Amount" : h);

// --- utils ---
const readJSON = (k, fb) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fb;
  } catch {
    return fb;
  }
};
const writeJSON = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};
const normalize = (v) =>
  v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);

const toISO = (d) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
const parseISO = (s) => {
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? null : d;
};
const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const diffDays = (aISO, bISO) => {
  const a = parseISO(aISO),
    b = parseISO(bISO);
  if (!a || !b) return NaN;
  return Math.floor((b - a) / 86400000);
};

const todayISO = () => toISO(new Date());
const firstOfMonthISO = () => {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return toISO(first);
};

// ---- sort helpers (why: robust, cross-browser date/number parsing) ----
const DATE_HEADERS = new Set(["Starts", "Ends", "Date Purchased"]);

const isNumeric = (s) =>
  typeof s === "number" ||
  (typeof s === "string" && s.trim() !== "" && !isNaN(Number(s)));

const parseCurrency = (s) => {
  if (typeof s !== "string") return NaN;
  const num = s.replace(/[^\d.\-]+/g, "");
  return Number(num);
};

// Safely parse "YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", or ISO strings
const parseDateLoose = (raw) => {
  if (typeof raw !== "string") return NaN;
  const s = raw.trim();
  // YYYY-MM-DD or YYYY-MM-DD HH:mm:ss
  const ymd = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;
  const m = s.match(ymd);
  if (m) {
    const [, Y, M, D, h = "00", mi = "00", se = "00"] = m;
    const d = new Date(
      Number(Y),
      Number(M) - 1,
      Number(D),
      Number(h),
      Number(mi),
      Number(se)
    );
    return isNaN(d.getTime()) ? NaN : d.getTime();
  }
  // Fallback for other date-like strings that Date.parse can handle
  const t = Date.parse(s.replace(" ", "T"));
  return isNaN(t) ? NaN : t;
};

const asComparable = (val, header) => {
  const raw = normalize(val);

  // Currency
  if (header === "Remit Amount") {
    const n = parseCurrency(raw);
    if (!isNaN(n)) return { type: "number", v: n };
  }

  // Date if header hints or value looks date-ish
  if (DATE_HEADERS.has(header)) {
    const t = parseDateLoose(raw);
    if (!isNaN(t)) return { type: "date", v: t };
  } else {
    const maybeDate = parseDateLoose(raw);
    if (!isNaN(maybeDate)) return { type: "date", v: maybeDate };
  }

  if (isNumeric(raw)) return { type: "number", v: Number(raw) };

  return { type: "string", v: String(raw).toLowerCase() };
};

const compareVals = (a, b, dir) => {
  if (a.type !== b.type) {
    // deterministic order across types
    const order = { number: 0, date: 1, string: 2 };
    const diff = order[a.type] - order[b.type];
    return dir === "asc" ? diff : -diff;
  }
  if (a.v < b.v) return dir === "asc" ? -1 : 1;
  if (a.v > b.v) return dir === "asc" ? 1 : -1;
  return 0;
};

export default function Reservations() {
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [secureToken, setSecureToken] = useState(null);
  const [expiresAtTs, setExpiresAtTs] = useState(null);
  const [expired, setExpired] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() =>
    readJSON("selected_facilities", [])
  );

  const cleanupTimerRef = useRef(null);
  const uiExpireTimerRef = useRef(null);

  // --- sort state (two-state: asc <-> desc) ---
  const [sort, setSort] = useState({ key: null, dir: "asc" }); // dir: 'asc' | 'desc'

  // ✅ Live update facility selection
  useEffect(() => {
    const updateFacilities = () => {
      const ids = readJSON("selected_facilities", []);
      setSelectedIds(ids);
      if (!Array.isArray(ids) || ids.length === 0) {
        setHeaders([]);
        setRows([]);
        setExpired(false);
        setExpiresAtTs(null);
      }
    };
    const onStorage = (e) => {
      if (e.key === "selected_facilities") updateFacilities();
    };
    const onCustom = () => updateFacilities();
    const onFocus = () => updateFacilities();

    window.addEventListener("storage", onStorage);
    window.addEventListener("selected_facilities_changed", onCustom);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("selected_facilities_changed", onCustom);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // --- cache hydrate ---
  useEffect(() => {
    const cache = readJSON(CACHE_KEY, null);
    if (cache?.headers && cache?.rows) {
      const remaining = EXPIRE_MS - (Date.now() - (cache.ts || 0));
      if (remaining > 0) {
        setHeaders(cache.headers);
        setRows(cache.rows);
        setPage(1);
        setSecureToken(cache.secure_token || null);
        scheduleCleanup(cache.secure_token, remaining);
        scheduleUiExpiry(remaining);
        setExpiresAtTs((cache.ts || 0) + EXPIRE_MS);
      } else {
        localStorage.removeItem(CACHE_KEY);
      }
    }
    return () => {
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
      if (uiExpireTimerRef.current) clearTimeout(uiExpireTimerRef.current);
    };
  }, []);

  function scheduleCleanup(token, delayMs = EXPIRE_MS) {
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    if (!token) return;
    cleanupTimerRef.current = setTimeout(async () => {
      try {
        await exportCleanup(token);
        const prev = readJSON(CACHE_KEY, {});
        writeJSON(CACHE_KEY, { ...prev, secure_token: null });
      } catch {}
    }, Math.max(0, delayMs));
  }

  function scheduleUiExpiry(delayMs = EXPIRE_MS) {
    if (uiExpireTimerRef.current) clearTimeout(uiExpireTimerRef.current);
    uiExpireTimerRef.current = setTimeout(() => {
      setExpired(true);
      setExpiresAtTs(null);
      setHeaders([]);
      setRows([]);
      localStorage.removeItem(CACHE_KEY);
    }, Math.max(0, delayMs));
    setExpired(false);
  }

  async function handleGetData(values) {
    if (!selectedIds?.length) {
      alert("Please select at least one facility from the sidebar.");
      return;
    }

    try {
      setLoading(true);
      if (secureToken) {
        try {
          await exportCleanup(secureToken);
        } catch {}
      }

      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
      if (uiExpireTimerRef.current) clearTimeout(uiExpireTimerRef.current);
      setHeaders([]);
      setRows([]);
      setExpired(false);
      setExpiresAtTs(null);
      localStorage.removeItem(CACHE_KEY);

      const body = {
        facility_ids: selectedIds,
        date_from: values.dateFrom,
        date_to: values.dateTo,
        export_type: "cp_operator_reservations",
        per_page: 1_000_000_000,
      };

      const res = await exportReservations(body);
      const data = res?.data || {};
      const processed = data?.processed_data || {};
      const hdrs = processed?.headers || [];
      const rws = processed?.rows || [];

      const cleanHeaders = hdrs.filter((h) => VISIBLE_COLUMNS.includes(h));
      const displayHeaders = cleanHeaders.map(DISPLAY_HEADER);

      const cleanRows =
        Array.isArray(rws) &&
        rws.map((r) =>
          Object.fromEntries(
            cleanHeaders.map((h, i) => [displayHeaders[i], r[h]])
          )
        );

      setHeaders(displayHeaders);
      setRows(Array.isArray(cleanRows) ? cleanRows : []);
      setPage(1);

      setSort({ key: null, dir: "asc" }); // reset sort on new data

      const token = data?.secure_token || null;
      setSecureToken(token);
      const ts = Date.now();
      setExpiresAtTs(ts + EXPIRE_MS);
      scheduleCleanup(token, EXPIRE_MS);
      scheduleUiExpiry(EXPIRE_MS);

      writeJSON(CACHE_KEY, {
        headers: displayHeaders,
        rows: cleanRows,
        secure_token: token,
        ts,
      });
    } catch (e) {
      console.error("❌ Fetch error:", e);
      alert("Failed to get data. See console for details.");
      setHeaders([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // Derived: filter → sort → paginate
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      headers.some((h) => normalize(r[h]).toLowerCase().includes(q))
    );
  }, [rows, headers, search]);

  // Stable index map (why: preserve original order for ties)
  const indexMap = useMemo(() => {
    const wm = new WeakMap();
    filteredRows.forEach((row, i) => wm.set(row, i));
    return wm;
  }, [filteredRows]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const { key, dir } = sort;
    return filteredRows.slice().sort((a, b) => {
      const av = asComparable(a[key], key);
      const bv = asComparable(b[key], key);
      const cmp = compareVals(av, bv, dir);
      if (cmp !== 0) return cmp;
      // stable tie-breaker
      return indexMap.get(a) - indexMap.get(b);
    });
  }, [filteredRows, sort, indexMap]);

  const totalRows = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = sortedRows.slice(start, start + PAGE_SIZE);

  function downloadCSV() {
    if (!headers.length || !sortedRows.length) return;
    const headerLine = headers.join(",");
    const csvLines = sortedRows.map((r) =>
      headers.map((h) => `"${normalize(r[h]).replace(/"/g, '""')}"`).join(",")
    );
    const csvContent = [headerLine, ...csvLines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reservations_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Countdown
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expiresAtTs) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAtTs]);
  const remainingMs = Math.max(0, (expiresAtTs ?? 0) - now);
  const mm = String(Math.floor(remainingMs / 60000)).padStart(2, "0");
  const ss = String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0");

  const initialValues = { dateFrom: firstOfMonthISO(), dateTo: todayISO() };
  const validate = (values) => {
    const errors = {};
    if (!values.dateFrom) errors.dateFrom = "Required";
    if (!values.dateTo) errors.dateTo = "Required";
    if (values.dateFrom && values.dateTo) {
      if (values.dateFrom > values.dateTo)
        errors.dateTo = "Must be on/after start date";
      else if (diffDays(values.dateFrom, values.dateTo) > 31)
        errors.dateTo = "Maximum range is 31 days";
    }
    return errors;
  };

  // If nothing selected
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border rounded-2xl shadow-sm p-6 flex flex-col items-center text-center space-y-4">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-indigo-50">
            <Frown className="h-10 w-10 text-blue-500" />
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            No facilities selected
          </h2>
          <p className="text-sm sm:text-base text-gray-600">
            Select at least one facility from the sidebar to view reservations.
          </p>
        </div>
      </div>
    );
  }

  const toggleSort = (key) => {
    setPage(1);
    setSort((s) =>
      s.key !== key
        ? { key, dir: "asc" }
        : { key, dir: s.dir === "asc" ? "desc" : "asc" }
    );
  };

  const SortIcon = ({ header }) => {
    if (sort.key !== header)
      return <ChevronsUpDown className="h-4 w-4 opacity-40" />;
    return sort.dir === "asc" ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F7F7FA] via-white to-[#F7F7FA] px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
      <motion.div
        className="mx-auto w-full max-w-7xl space-y-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="text-[28px] sm:text-[32px] font-semibold text-gray-900">
              All Reservations
            </h1>
            <p className="text-xs text-gray-500">
              {Array.isArray(selectedIds) && selectedIds.length
                ? `${selectedIds.length} facilit${
                    selectedIds.length > 1 ? "ies" : "y"
                  } selected`
                : "No facilities selected"}
            </p>
          </div>

          {/* Form */}
          <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleGetData}
            enableReinitialize
          >
            {({ errors, touched, isSubmitting, isValid, values }) => {
              const from = parseISO(values.dateFrom);
              const maxTo = from ? toISO(addDays(from, 31)) : undefined;
              return (
                <Form className="grid grid-cols-1 sm:grid-cols-4 gap-2 w-full md:w-auto">
                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Field
                      name="dateFrom"
                      type="date"
                      max={values.dateTo || undefined}
                      className={`w-full pl-9 pr-3 py-2 rounded-xl border bg-white text-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${
                        errors.dateFrom && touched.dateFrom
                          ? "border-red-400"
                          : ""
                      }`}
                    />
                    {errors.dateFrom && touched.dateFrom && (
                      <div className="mt-1 text-xs text-red-600">
                        {errors.dateFrom}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Field
                      name="dateTo"
                      type="date"
                      min={values.dateFrom || undefined}
                      max={maxTo}
                      className={`w-full pl-9 pr-3 py-2 rounded-xl border bg-white text-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600 ${
                        errors.dateTo && touched.dateTo ? "border-red-400" : ""
                      }`}
                    />
                    {errors.dateTo && touched.dateTo && (
                      <div className="mt-1 text-xs text-red-600">
                        {errors.dateTo}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !isValid || isSubmitting}
                    className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-white font-semibold shadow transition ${
                      loading || !isValid || isSubmitting
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {loading ? "Getting Data…" : "Get Data"}
                  </button>

                  {rows.length > 0 && (
                    <button
                      type="button"
                      onClick={downloadCSV}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold shadow hover:bg-gray-800 transition"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </button>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>

        {/* Expiry Note */}
        {headers.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-xl border bg-amber-50 text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium">Heads up:</span> This data expires
              in{" "}
              <span className="font-semibold">
                {mm}:{ss}
              </span>
              . Please download if you need to keep a copy.
            </div>
          </div>
        )}

        {/* Search */}
        {rows.length > 0 && (
          <div className="flex justify-end">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search reservations…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border bg-white text-sm focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
              />
            </div>
          </div>
        )}

        {/* Table/Card */}
        <div className="bg-white rounded-2xl shadow-sm border">
          {/* Desktop Table */}
          <div className="hidden md:block">
            <table className="table-fixed w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {headers.map((h) => {
                    const isActive = sort.key === h;
                    const ariaSort = isActive
                      ? sort.dir === "asc"
                        ? "ascending"
                        : "descending"
                      : "none";
                    return (
                      <th
                        key={h}
                        aria-sort={ariaSort}
                        className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wide select-none"
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(h)}
                          className="group inline-flex items-center gap-1 hover:text-gray-900 focus:outline-none"
                          title={
                            isActive
                              ? sort.dir === "asc"
                                ? "Sorted ascending. Click for descending."
                                : "Sorted descending. Click for ascending."
                              : "Click to sort ascending."
                          }
                        >
                          <span>{h}</span>
                          <SortIcon header={h} />
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y">
                {pageRows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 align-top">
                    {headers.map((h) => (
                      <td
                        key={h}
                        className="px-4 py-3 text-gray-800 whitespace-normal break-words"
                      >
                        {normalize(r[h])}
                      </td>
                    ))}
                  </tr>
                ))}

                {sortedRows.length === 0 && (
                  <tr>
                    <td
                      className="px-6 py-10 text-center text-gray-500"
                      colSpan={Math.max(headers.length, 1)}
                    >
                      {expired
                        ? "The previous data has expired. Please click Get Data to refresh."
                        : loading
                        ? "Fetching data…"
                        : "No data. Pick dates and click Get Data."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-3">
            {pageRows.map((r, i) => (
              <div key={i} className="rounded-xl border bg-white shadow-sm p-4">
                <div className="grid grid-cols-1 gap-1 text-sm">
                  {headers.map((h) => (
                    <div key={h} className="flex justify-between gap-3">
                      <span className="text-gray-500">{h}</span>
                      <span className="font-medium text-gray-900 text-right break-words max-w-[55%]">
                        {normalize(r[h])}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {sortedRows.length === 0 && (
              <div className="rounded-xl border bg-white p-6 text-center text-gray-500">
                {expired
                  ? "The previous data has expired. Please click Get Data to refresh."
                  : loading
                  ? "Fetching data…"
                  : "No data. Pick dates and click Get Data."}
              </div>
            )}
          </div>

          {/* Pagination */}
          {sortedRows.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 text-xs text-gray-600">
              <span>
                Showing {start + 1}–{Math.min(start + PAGE_SIZE, totalRows)} of{" "}
                {totalRows}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`px-3 py-1.5 rounded-lg border ${
                    page <= 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  Prev
                </button>
                <span className="px-2 py-1 rounded-md bg-white border font-medium">
                  {page}
                </span>
                <span>of</span>
                <span className="px-2 py-1 rounded-md bg-white border font-medium">
                  {Math.max(totalPages, 1)}
                </span>
                <button
                  onClick={() =>
                    setPage((p) => Math.min(Math.max(totalPages, 1), p + 1))
                  }
                  disabled={page >= totalPages}
                  className={`px-3 py-1.5 rounded-lg border ${
                    page >= totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
